import { GameState, IPiece, Mode, State } from '../../types';
import { Piece } from '../../Piece';
import { GameStateManager } from '../../Managers/GameStateManager';
import { MovementManager } from '../../Managers/MovementManager';
import { BonusTurnManager } from '../../Managers/BonusTurnManager';
import { TurnController } from '../../Managers/TurnController';
import { FENHistoryManager } from '../../Managers/FENHistoryManager';
import { EventManager } from '../../Managers/EventManager';
import { MoveInfo } from '../Core/CommonAIUtils';

export type AstraState = GameState & { astraPendingFens?: string[] };

// Search never shares mutable piece/status objects with the live board.
export function copyState(state: GameState): GameState {
  const data = JSON.parse(JSON.stringify(state, (key, value) => key === 'movementStrategy' ? undefined : value));
  data.board = data.board.map((row: any[]) => row.map(square => ({ Pieces: square.Pieces.map((p: IPiece) => {
    const piece = new Piece(p.type, p.isLight, p.direction);
    Object.assign(piece, p);
    return piece;
  }) })));
  return data;
}

export function moveKey(info: MoveInfo): string {
  const m = info.move;
  return `${m.pieceId}:${info.fromPosition.row},${info.fromPosition.col}:${m.position.row},${m.position.col}:${m.moveType}:${m.toPiece?.id || ''}`;
}

// Include full piece types/IDs, rule flags, turn counter and repetition history.
// Bounds are only reused for the identical rule state, never just a similar board.
export function stateKey(state: GameState): string {
  return JSON.stringify({
    board: state.board.map(row => row.map(s => s.Pieces.map(p => {
      const { movementStrategy, ...flags } = p;
      return flags;
    }))),
    turn: state.isLightTurn, count: state.turnCount, state: state.state,
    kings: [state.lightKing, state.darkKing], bonus: state.activeBonusTurnPieceId,
    skip: state.skipTurnChange, swing: [state.lastSwingMonkeyId, state.lastSwingFromPosition, state.lastSwingToPosition],
    pendingZombie: (state as any).pendingMonkeyZombification, shadows: state.shadowsActive,
    history: state.fenHistory, pendingHistory: (state as AstraState).astraPendingFens || [],
    pendingZombification: (state as any).pendingZombification,
  });
}

class NeedDie extends Error {}
export interface Outcome { state: GameState; probability: number }

/** Isolated, synchronous use of production rules: no clock, AI scheduling or remote writes. */
export class AstraRules {
  private managers(state: GameState, dice: number[] = []) {
    const gsm = new GameStateManager();
    gsm.setGameState(state);
    const bonus = new BonusTurnManager(gsm);
    const history = new FENHistoryManager();
    const turn = new TurnController(gsm, bonus, history);
    const events = new EventManager(gsm);
    events.disableRemoteSync = true;
    let dieIndex = 0;
    const movement = new MovementManager(gsm, events, bonus, history, turn, () => {
      if (dieIndex >= dice.length) throw new NeedDie();
      return dice[dieIndex++];
    });
    movement.restorePendingTurnFENs((state as AstraState).astraPendingFens || []);
    movement.setShadowsActive(!!state.shadowsActive || state.board.some(r => r.some(s => s.Pieces.some(p => p.isShadow))));
    return { gsm, bonus, turn, movement };
  }

  legalMoves(state: GameState): MoveInfo[] {
    const { movement } = this.managers(state);
    const moves: MoveInfo[] = [];
    state.board.forEach((row, r) => row.forEach((square, c) => square.Pieces.forEach(piece => {
      if (piece.isShadow || (piece.isLight !== state.isLightTurn && !piece.zombifiedStatus.isZombified)) return;
      // MovementManager enforces bonus locks and temporary zombie ownership.
      for (const move of movement.getValidMoves({row:r,col:c}, piece)) {
        moves.push({ fromPosition: {row:r,col:c}, move });
      }
    })));
    return moves;
  }

  outcomes(state: GameState, info: MoveInfo): Outcome[] {
    const result: Outcome[] = [];
    const visit = (dice: number[], probability: number): void => {
      const next = copyState(state);
      // Preserve TestAI's deterministic leap rule; other modes use normal dice.
      next.mode = state.mode === Mode.TestAI ? Mode.TestAI : Mode.AI;
      delete (next as any).tutorialConfig;
      const { gsm, bonus, turn, movement } = this.managers(next, dice);
      const pieces = new Map<string, IPiece>();
      next.board.forEach(row => row.forEach(s => s.Pieces.forEach(p => pieces.set(p.id, p))));
      const piece = pieces.get(info.move.pieceId);
      if (!piece) return;
      const effects = info.move.sideEffects.map(se => ({ ...se, position: {...se.position}, effect: {...se.effect},
        piecesToEffect: se.piecesToEffect.map(p => pieces.get(p.id)).filter((p): p is IPiece => !!p) }));
      try {
        if (!movement.makeMove(info.fromPosition, info.move.position, piece,
          info.move.toPiece ? pieces.get(info.move.toPiece.id) || null : null, info.move.moveType, effects)) return;
        if (gsm.hasGameEnded()) {
          bonus.clearBonusTurn();
          if (next.state !== State.Draw) next.state = State.PostGame;
        } else {
          turn.changeTurns(() => movement.finalizeTurnFENHistory(), () => {}, () => {});
        }
        // Match ZodiacEngine.runPostTurn, including expiry after bonus moves.
        gsm.getAllPieces().forEach(p => {
          if (p.poisonedStatus.removeOnTurn === next.turnCount) p.poisonedStatus = {isPoisoned:false};
          if (!next.activeBonusTurnPieceId && p.zombifiedStatus.removeOnTurn === next.turnCount) {
            p.zombifiedStatus = {isZombified:false}; p.isZombified = false;
          }
        });
        (next as AstraState).astraPendingFens = movement.getPendingTurnFENs();
        result.push({state:next, probability});
      } catch (error) {
        if (!(error instanceof NeedDie)) throw error;
        if (dice.length >= 2) throw new Error('Unexpected third dice roll in Zodiac move');
        for (let die = 1; die <= 6; die++) visit([...dice, die], probability / 6);
      }
    };
    visit([], 1);
    return result;
  }
}

export function describeMove(state: GameState, info: MoveInfo): string {
  const p = state.board[info.fromPosition.row][info.fromPosition.col].Pieces.find(p => p.id === info.move.pieceId);
  const square = (r: number, c: number) => `${String.fromCharCode(65+c)}${r+1}`;
  return `${p?.type || 'Piece'} ${square(info.fromPosition.row,info.fromPosition.col)}-${square(info.move.position.row,info.move.position.col)} (${info.move.moveType})`;
}
